import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../config/API";

function NewUserRegister() {
  const navigate = useNavigate();

  // ======================================================
  // USER FORM
  // ======================================================

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    contactNo: "",
    emailId: "",
    gender: "",
  });

  // ======================================================
  // VALIDATION ERRORS
  // ======================================================

  const [errors, setErrors] = useState({});

  // ======================================================
  // LOADING
  // ======================================================

  const [loading, setLoading] = useState(false);

  // ======================================================
  // NORMAL POPUP
  // ======================================================

  const [popup, setPopup] = useState({
    show: false,
    message: "",
    type: "",
  });

  // ======================================================
  // OTP POPUP
  // ======================================================

  const [otpPopup, setOtpPopup] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  // ======================================================
  // REGISTERED USER DETAILS
  // ======================================================

  const [registeredUser, setRegisteredUser] = useState(null);

  // ======================================================
  // SHOW POPUP
  // ======================================================

  const showPopup = (message, type) => {
    setPopup({
      show: true,
      message,
      type,
    });

    setTimeout(() => {
      setPopup({
        show: false,
        message: "",
        type: "",
      });
    }, 3000);
  };

  // ======================================================
  // DEVICE TYPE
  // ======================================================

  const getDeviceType = () => {
    const userAgent = navigator.userAgent || "";

    if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
      return "Tablet";
    }

    if (/android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
      return "Mobile";
    }

    return "Desktop";
  };

  // ======================================================
  // OS NAME
  // ======================================================

  const getOSName = () => {
    const userAgent = navigator.userAgent || "";

    if (/Windows NT/i.test(userAgent)) {
      return "Windows";
    }

    if (/Android/i.test(userAgent)) {
      return "Android";
    }

    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      return "iOS";
    }

    if (/Mac OS X/i.test(userAgent)) {
      return "macOS";
    }

    if (/Linux/i.test(userAgent)) {
      return "Linux";
    }

    return "Unknown";
  };

  // ======================================================
  // BROWSER NAME
  // ======================================================

  const getBrowserName = () => {
    const userAgent = navigator.userAgent || "";

    if (/Edg\//i.test(userAgent)) {
      return "Microsoft Edge";
    }

    if (/OPR\//i.test(userAgent)) {
      return "Opera";
    }

    if (/Chrome\//i.test(userAgent) && !/Edg\//i.test(userAgent)) {
      return "Google Chrome";
    }

    if (/Firefox\//i.test(userAgent)) {
      return "Mozilla Firefox";
    }

    if (
      /Safari\//i.test(userAgent) &&
      !/Chrome\//i.test(userAgent) &&
      !/Edg\//i.test(userAgent)
    ) {
      return "Safari";
    }

    return "Unknown";
  };

  // ======================================================
  // BROWSER VERSION
  // ======================================================

  const getBrowserVersion = () => {
    const userAgent = navigator.userAgent || "";

    let match;

    // Edge
    match = userAgent.match(/Edg\/([\d.]+)/i);

    if (match) {
      return match[1];
    }

    // Opera
    match = userAgent.match(/OPR\/([\d.]+)/i);

    if (match) {
      return match[1];
    }

    // Chrome
    match = userAgent.match(/Chrome\/([\d.]+)/i);

    if (match) {
      return match[1];
    }

    // Firefox
    match = userAgent.match(/Firefox\/([\d.]+)/i);

    if (match) {
      return match[1];
    }

    // Safari
    match = userAgent.match(/Version\/([\d.]+).*Safari/i);

    if (match) {
      return match[1];
    }

    return "Unknown";
  };

  // ======================================================
  // HANDLE FORM CHANGE
  // ======================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    // --------------------------------------------------
    // CONTACT NUMBER
    // --------------------------------------------------

    if (name === "contactNo") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // --------------------------------------------------
    // FIRST NAME / LAST NAME
    // --------------------------------------------------

    if (name === "firstName" || name === "lastName") {
      updatedValue = value.replace(/[^a-zA-Z\s]/g, "");
    }

    // --------------------------------------------------
    // EMAIL
    // --------------------------------------------------

    if (name === "emailId") {
      updatedValue = value.trimStart();
    }

    // --------------------------------------------------
    // SET FORM
    // --------------------------------------------------

    setForm((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    // --------------------------------------------------
    // CLEAR ERROR
    // --------------------------------------------------

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ======================================================
  // VALIDATE FORM
  // ======================================================

  const validateForm = () => {
    const tempErrors = {};

    // --------------------------------------------------
    // FIRST NAME
    // --------------------------------------------------

    if (!form.firstName.trim()) {
      tempErrors.firstName = "First Name is required";
    } else if (form.firstName.trim().length < 2) {
      tempErrors.firstName = "First Name must contain at least 2 characters";
    }

    // --------------------------------------------------
    // LAST NAME
    // --------------------------------------------------

    if (!form.lastName.trim()) {
      tempErrors.lastName = "Last Name is required";
    } else if (form.lastName.trim().length < 2) {
      tempErrors.lastName = "Last Name must contain at least 2 characters";
    }

    // --------------------------------------------------
    // CONTACT NUMBER
    // --------------------------------------------------

    if (!form.contactNo) {
      tempErrors.contactNo = "Contact Number is required";
    } else if (form.contactNo.length !== 10) {
      tempErrors.contactNo = "Contact Number must be exactly 10 digits";
    } else if (!/^[6-9]\d{9}$/.test(form.contactNo)) {
      tempErrors.contactNo = "Enter a valid Indian mobile number";
    }

    // --------------------------------------------------
    // EMAIL
    // --------------------------------------------------

    if (!form.emailId.trim()) {
      tempErrors.emailId = "Email ID is required";
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
        form.emailId.trim(),
      )
    ) {
      tempErrors.emailId = "Enter a valid email address";
    }

    // --------------------------------------------------
    // GENDER
    // --------------------------------------------------

    if (!form.gender) {
      tempErrors.gender = "Please select gender";
    }

    setErrors(tempErrors);

    return Object.keys(tempErrors).length === 0;
  };

  // ======================================================
  // CREATE NEW USER
  // ======================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // --------------------------------------------------
      // PAYLOAD
      // --------------------------------------------------

      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        contactNo: form.contactNo,
        emailId: form.emailId.trim(),
        gender: form.gender,

        deviceType: getDeviceType(),
        osName: getOSName(),
        browserName: getBrowserName(),
        browserVersion: getBrowserVersion(),

        // MAC address cannot be accessed from normal browser
        macId: null,
      };

      console.log("================================");
      console.log("CREATE NEW USER PAYLOAD");
      console.log("================================");
      console.log(payload);

      // --------------------------------------------------
      // DYNAMIC API URL
      // --------------------------------------------------

      const createUserUrl = `${API.V2}/pgOwner/createNewUser`;

      console.log("Create User API:", createUserUrl);

      // --------------------------------------------------
      // CREATE USER API
      // --------------------------------------------------

      const response = await axios.post(createUserUrl, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("================================");
      console.log("CREATE USER RESPONSE");
      console.log("================================");
      console.log(response.data);

      // ==================================================
      // SUCCESS
      // ==================================================

      if (response.data?.statusCode === 200) {
        // ------------------------------------------------
        // customId contains userRegisterId
        // ------------------------------------------------

        const userRegisterId = response.data?.customId;

        console.log("User Register ID:", userRegisterId);

        if (!userRegisterId) {
          showPopup(
            "User created but user registration ID was not returned.",
            "error",
          );

          return;
        }

        // ------------------------------------------------
        // SUCCESS MESSAGE
        // ------------------------------------------------

        showPopup(
          response.data?.message ||
            "User created successfully. OTP sent to mobile and email.",
          "success",
        );

        // ------------------------------------------------
        // SAVE REGISTERED USER DETAILS
        // ------------------------------------------------

        setRegisteredUser({
          userRegisterId: userRegisterId,
          contactNo: form.contactNo,
          emailId: form.emailId.trim(),
        });

        console.log("Registered User:", {
          userRegisterId: userRegisterId,
          contactNo: form.contactNo,
          emailId: form.emailId.trim(),
        });

        // ------------------------------------------------
        // CLEAR OTP
        // ------------------------------------------------

        setOtp("");

        // ------------------------------------------------
        // OPEN OTP POPUP
        // ------------------------------------------------

        setOtpPopup(true);

        // ------------------------------------------------
        // CLEAR FORM
        // ------------------------------------------------

        setForm({
          firstName: "",
          lastName: "",
          contactNo: "",
          emailId: "",
          gender: "",
        });

        setErrors({});
      } else {
        showPopup(response.data?.message || "Unable to create user.", "error");
      }
    } catch (error) {
      console.error("New User Registration Error:", error);

      console.error("Error Response:", error.response?.data);

      // --------------------------------------------------
      // 401
      // --------------------------------------------------

      if (error.response?.status === 401) {
        showPopup("Session expired. Please login again.", "error");

        localStorage.clear();

        setTimeout(() => {
          navigate("/");
        }, 1500);

        return;
      }

      // --------------------------------------------------
      // 409
      // --------------------------------------------------

      if (error.response?.status === 409) {
        showPopup(
          error.response?.data?.message ||
            "Email ID or contact number already exists.",
          "error",
        );

        return;
      }

      // --------------------------------------------------
      // OTHER ERROR
      // --------------------------------------------------

      showPopup(
        error.response?.data?.message ||
          "Something went wrong while creating user.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // ======================================================
  // OTP CHANGE
  // ======================================================

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);

    setOtp(value);
  };

  // ======================================================
  // VERIFY OTP
  // ======================================================

  const handleVerifyOtp = async () => {
    console.log("========== VERIFY OTP BUTTON CLICKED ==========");

    // --------------------------------------------------
    // VALIDATE OTP
    // --------------------------------------------------

    if (!otp || otp.length !== 6) {
      showPopup("Please enter a valid 6 digit OTP.", "error");

      return;
    }

    // --------------------------------------------------
    // CHECK REGISTERED USER
    // --------------------------------------------------

    if (!registeredUser?.userRegisterId) {
      console.log("Registered User:", registeredUser);

      showPopup("User registration details not found.", "error");

      return;
    }

    try {
      setOtpLoading(true);

      // ------------------------------------------------
      // OTP PAYLOAD
      // ------------------------------------------------

      const payload = {
        customId: registeredUser.userRegisterId,
        otp: otp,
      };

      console.log("========================================");
      console.log("OTP VERIFICATION PAYLOAD");
      console.log("========================================");
      console.log(payload);

      // ------------------------------------------------
      // DYNAMIC VERIFY OTP URL
      // ------------------------------------------------

      const verifyOtpUrl = `${API.V2}/usersDashboard/verifyLoginOtp`;

      console.log("Verify OTP API:", verifyOtpUrl);

      // ------------------------------------------------
      // VERIFY OTP API
      // ------------------------------------------------

      const response = await axios.post(verifyOtpUrl, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("========================================");
      console.log("OTP VERIFICATION RESPONSE");
      console.log("========================================");
      console.log(response.data);

      // ==================================================
      // OTP SUCCESS
      // ==================================================

      if (response.data?.statusCode === 200) {
        console.log("OTP verification successful");

        // ------------------------------------------------
        // GET RESPONSE DATA
        // ------------------------------------------------

        const responseData = response.data?.data;

        // ------------------------------------------------
        // CHECK JWT TOKEN
        // ------------------------------------------------

        const token =
          responseData?.token ||
          responseData?.jwtToken ||
          responseData?.accessToken;

        if (token) {
          console.log("JWT token received");

          localStorage.setItem("userToken", token);

          localStorage.setItem("jwtToken", token);
        } else {
          console.log("No JWT token found in response");
        }

        // ------------------------------------------------
        // SAVE USER REGISTER ID
        // ------------------------------------------------

        localStorage.setItem(
          "userRegisterId",
          String(registeredUser.userRegisterId),
        );

        // ------------------------------------------------
        // SAVE USER CONTACT
        // ------------------------------------------------

        localStorage.setItem("userContactNo", registeredUser.contactNo);

        // ------------------------------------------------
        // SAVE USER EMAIL
        // ------------------------------------------------

        localStorage.setItem("userEmailId", registeredUser.emailId);

        // ------------------------------------------------
        // CLOSE OTP POPUP
        // ------------------------------------------------

        setOtpPopup(false);
        setOtp("");

        // ------------------------------------------------
        // SUCCESS MESSAGE
        // ------------------------------------------------

        showPopup(
          response.data?.message ||
            "OTP verified successfully. Login successful.",
          "success",
        );

        // ------------------------------------------------
        // NAVIGATE
        // ------------------------------------------------

        setTimeout(() => {
          navigate("/usersDashboardPage");
        }, 1500);
      } else {
        showPopup(response.data?.message || "Invalid OTP.", "error");
      }
    } catch (error) {
      console.error("OTP Verification Error:", error);

      console.error("OTP Error Response:", error.response?.data);

      console.error("OTP Error Status:", error.response?.status);

      if (error.response?.status === 401) {
        showPopup("Session expired. Please login again.", "error");

        localStorage.clear();

        setTimeout(() => {
          navigate("/");
        }, 1500);

        return;
      }

      showPopup(
        error.response?.data?.message || "Unable to verify OTP.",
        "error",
      );
    } finally {
      setOtpLoading(false);
    }
  };

  // ======================================================
  // CLOSE OTP POPUP
  // ======================================================

  const closeOtpPopup = () => {
    if (otpLoading) {
      return;
    }

    setOtpPopup(false);
    setOtp("");
  };

  // ======================================================
  // RESET FORM
  // ======================================================

  const handleReset = () => {
    setForm({
      firstName: "",
      lastName: "",
      contactNo: "",
      emailId: "",
      gender: "",
    });

    setErrors({});
  };

  // ======================================================
  // UI
  // ======================================================

  return (
    <div style={styles.page}>
      {/* ==================================================
          NORMAL POPUP
      ================================================== */}

      {popup.show && (
        <div
          style={{
            ...styles.popup,
            background: popup.type === "success" ? "#16a34a" : "#dc2626",
          }}
        >
          {popup.type === "success" ? "✓ " : "⚠ "}

          {popup.message}
        </div>
      )}

      {/* ==================================================
          OTP POPUP
      ================================================== */}

      {otpPopup && (
        <div style={styles.modalOverlay}>
          <div style={styles.otpModal}>
            {/* OTP ICON */}

            <div style={styles.otpIcon}>🔐</div>

            {/* TITLE */}

            <h2 style={styles.otpTitle}>Verify OTP</h2>

            {/* DESCRIPTION */}

            <p style={styles.otpSubtitle}>
              A 6-digit OTP has been sent to your mobile number and email.
            </p>

            {/* MOBILE */}

            {registeredUser?.contactNo && (
              <p style={styles.sentInfo}>
                📱 OTP sent to <strong>{registeredUser.contactNo}</strong>
              </p>
            )}

            {/* EMAIL */}

            {registeredUser?.emailId && (
              <p style={styles.sentInfo}>
                ✉️ OTP sent to <strong>{registeredUser.emailId}</strong>
              </p>
            )}

            {/* OTP INPUT */}

            <div style={styles.otpField}>
              <label style={styles.otpLabel}>Enter OTP</label>

              <input
                type="text"
                value={otp}
                onChange={handleOtpChange}
                placeholder="Enter 6 digit OTP"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                style={styles.otpInput}
              />
            </div>

            {/* OTP LENGTH MESSAGE */}

            {otp.length > 0 && otp.length !== 6 && (
              <p style={styles.otpHint}>Please enter all 6 digits.</p>
            )}

            {/* VERIFY BUTTON */}

            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={otpLoading || otp.length !== 6}
              style={{
                ...styles.verifyOtpButton,
                ...(otpLoading || otp.length !== 6
                  ? styles.disabledButton
                  : {}),
              }}
            >
              {otpLoading ? "Verifying..." : "Verify OTP"}
            </button>

            {/* CANCEL */}

            <button
              type="button"
              onClick={closeOtpPopup}
              disabled={otpLoading}
              style={styles.cancelOtpButton}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ==================================================
          MAIN CONTAINER
      ================================================== */}

      <div style={styles.container}>
        {/* ==================================================
            HEADER
        ================================================== */}

        <div style={styles.header}>
          <div style={styles.iconCircle}>👤</div>

          <h1 style={styles.title}>Create New User</h1>

          <p style={styles.subtitle}>
            Enter the user details to create a new account
          </p>
        </div>

        {/* ==================================================
            FORM
        ================================================== */}

        <form onSubmit={handleSubmit}>
          <div style={styles.grid}>
            {/* FIRST NAME */}

            <div style={styles.cardBlue}>
              <label style={styles.label}>
                First Name <span style={styles.required}>*</span>
              </label>

              <input
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                placeholder="Enter first name"
                style={{
                  ...styles.input,
                  ...(errors.firstName ? styles.inputError : {}),
                }}
              />

              {errors.firstName && (
                <p style={styles.error}>{errors.firstName}</p>
              )}
            </div>

            {/* LAST NAME */}

            <div style={styles.cardGreen}>
              <label style={styles.label}>
                Last Name <span style={styles.required}>*</span>
              </label>

              <input
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                placeholder="Enter last name"
                style={{
                  ...styles.input,
                  ...(errors.lastName ? styles.inputError : {}),
                }}
              />

              {errors.lastName && <p style={styles.error}>{errors.lastName}</p>}
            </div>

            {/* CONTACT NUMBER */}

            <div style={styles.cardPurple}>
              <label style={styles.label}>
                Contact Number <span style={styles.required}>*</span>
              </label>

              <input
                type="text"
                name="contactNo"
                value={form.contactNo}
                onChange={handleChange}
                placeholder="Enter 10 digit mobile number"
                maxLength={10}
                inputMode="numeric"
                style={{
                  ...styles.input,
                  ...(errors.contactNo ? styles.inputError : {}),
                }}
              />

              {errors.contactNo && (
                <p style={styles.error}>{errors.contactNo}</p>
              )}
            </div>

            {/* EMAIL */}

            <div style={styles.cardOrange}>
              <label style={styles.label}>
                Email ID <span style={styles.required}>*</span>
              </label>

              <input
                type="email"
                name="emailId"
                value={form.emailId}
                onChange={handleChange}
                placeholder="Enter email address"
                style={{
                  ...styles.input,
                  ...(errors.emailId ? styles.inputError : {}),
                }}
              />

              {errors.emailId && <p style={styles.error}>{errors.emailId}</p>}
            </div>

            {/* GENDER */}

            <div style={styles.cardPink}>
              <label style={styles.label}>
                Gender <span style={styles.required}>*</span>
              </label>

              <select
                name="gender"
                value={form.gender}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.gender ? styles.inputError : {}),
                }}
              >
                <option value="">Select Gender</option>

                <option value="Male">Male</option>

                <option value="Female">Female</option>

                <option value="Other">Other</option>
              </select>

              {errors.gender && <p style={styles.error}>{errors.gender}</p>}
            </div>
          </div>

          {/* ==================================================
              BUTTONS
          ================================================== */}

          <div style={styles.buttonContainer}>
            <button
              type="button"
              onClick={handleReset}
              disabled={loading}
              style={styles.resetButton}
            >
              Reset
            </button>

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.submitButton,
                ...(loading ? styles.disabledButton : {}),
              }}
            >
              {loading ? "Creating User..." : "Create User"}
            </button>
          </div>
        </form>

        {/* ==================================================
            LOGIN
        ================================================== */}

        <div style={styles.loginSection}>
          <span>Already have an account?</span>

          <button
            type="button"
            onClick={() => navigate("/userLogin")}
            style={styles.loginButton}
          >
            Login
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewUserRegister;

// ======================================================
// STYLES
// ======================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #eff6ff, #f8fafc, #e0f2fe)",
    padding: "30px 20px",
    fontFamily: "Segoe UI, Arial, sans-serif",
    boxSizing: "border-box",
  },

  container: {
    width: "100%",
    maxWidth: "900px",
    margin: "0 auto",
    background: "#ffffff",
    borderRadius: "24px",
    padding: "35px",
    boxSizing: "border-box",
    boxShadow: "0 20px 50px rgba(15, 23, 42, 0.12)",
  },

  header: {
    textAlign: "center",
    marginBottom: "35px",
  },

  iconCircle: {
    width: "75px",
    height: "75px",
    margin: "0 auto 15px",
    borderRadius: "50%",
    background: "#dbeafe",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "35px",
  },

  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "32px",
    fontWeight: "800",
  },

  subtitle: {
    marginTop: "8px",
    color: "#64748b",
    fontSize: "15px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
  },

  cardBlue: {
    background: "#dbeafe",
    padding: "20px",
    borderRadius: "16px",
  },

  cardGreen: {
    background: "#dcfce7",
    padding: "20px",
    borderRadius: "16px",
  },

  cardPurple: {
    background: "#ede9fe",
    padding: "20px",
    borderRadius: "16px",
  },

  cardOrange: {
    background: "#ffedd5",
    padding: "20px",
    borderRadius: "16px",
  },

  cardPink: {
    background: "#fce7f3",
    padding: "20px",
    borderRadius: "16px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    color: "#1e293b",
    fontSize: "14px",
    fontWeight: "700",
  },

  required: {
    color: "#dc2626",
  },

  input: {
    width: "100%",
    height: "48px",
    padding: "0 14px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    outline: "none",
    background: "#fff",
    color: "#0f172a",
    fontSize: "15px",
    boxSizing: "border-box",
  },

  inputError: {
    border: "2px solid #dc2626",
  },

  error: {
    margin: "7px 0 0",
    color: "#dc2626",
    fontSize: "12px",
    fontWeight: "600",
  },

  buttonContainer: {
    display: "flex",
    gap: "15px",
    marginTop: "30px",
  },

  resetButton: {
    flex: 1,
    height: "52px",
    border: "none",
    borderRadius: "12px",
    background: "#64748b",
    color: "#fff",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
  },

  submitButton: {
    flex: 2,
    height: "52px",
    border: "none",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "#fff",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(37, 99, 235, 0.25)",
  },

  disabledButton: {
    background: "#94a3b8",
    cursor: "not-allowed",
    boxShadow: "none",
  },

  loginSection: {
    marginTop: "25px",
    paddingTop: "20px",
    borderTop: "1px solid #e2e8f0",
    textAlign: "center",
    color: "#64748b",
    fontSize: "14px",
  },

  loginButton: {
    marginLeft: "8px",
    border: "none",
    background: "transparent",
    color: "#2563eb",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "14px",
  },

  // ====================================================
  // NORMAL POPUP
  // ====================================================

  popup: {
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 999999,
    padding: "14px 22px",
    borderRadius: "10px",
    color: "#fff",
    fontWeight: "700",
    boxShadow: "0 10px 25px rgba(0,0,0,.25)",
    maxWidth: "400px",
  },

  // ====================================================
  // OTP OVERLAY
  // ====================================================

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.65)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 100000,
    padding: "20px",
    boxSizing: "border-box",
  },

  // ====================================================
  // OTP MODAL
  // ====================================================

  otpModal: {
    width: "100%",
    maxWidth: "420px",
    background: "#ffffff",
    borderRadius: "20px",
    padding: "30px",
    boxSizing: "border-box",
    boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
  },

  otpIcon: {
    width: "65px",
    height: "65px",
    margin: "0 auto 15px",
    borderRadius: "50%",
    background: "#dbeafe",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "30px",
  },

  otpTitle: {
    margin: 0,
    textAlign: "center",
    color: "#0f172a",
    fontSize: "25px",
    fontWeight: "800",
  },

  otpSubtitle: {
    margin: "10px 0 15px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "14px",
    lineHeight: "1.5",
  },

  sentInfo: {
    margin: "5px 0",
    textAlign: "center",
    color: "#475569",
    fontSize: "13px",
  },

  otpField: {
    marginTop: "22px",
  },

  otpLabel: {
    display: "block",
    marginBottom: "8px",
    color: "#1e293b",
    fontSize: "14px",
    fontWeight: "700",
  },

  otpInput: {
    width: "100%",
    height: "55px",
    padding: "0 15px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    outline: "none",
    fontSize: "24px",
    fontWeight: "700",
    letterSpacing: "8px",
    textAlign: "center",
    boxSizing: "border-box",
    color: "#0f172a",
  },

  otpHint: {
    margin: "8px 0 0",
    textAlign: "center",
    color: "#dc2626",
    fontSize: "12px",
    fontWeight: "600",
  },

  verifyOtpButton: {
    width: "100%",
    height: "50px",
    border: "none",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "#ffffff",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
    marginTop: "25px",
    boxShadow: "0 8px 20px rgba(37, 99, 235, 0.20)",
  },

  cancelOtpButton: {
    width: "100%",
    height: "45px",
    border: "none",
    background: "transparent",
    color: "#64748b",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "8px",
  },
};
